import type { CSSProperties, ChangeEvent, KeyboardEvent, MouseEvent, ReactNode } from 'react';
import { Fragment, useEffect, useLayoutEffect, useRef, useState } from 'react';

import type { BasicValue, Classnames } from '@snowit/icetypes';
import type { IInput } from '../Input/Input';
import type { IInputOption } from '../InputSelect/InputSelect';

import useBreakpoints from '@snowit/hooks/useBreakpoints';
import Portal from '@snowit/hooks/usePortals';
import { BadgeLabel } from '@snowit/ui';
import ButtonIcon from '@snowit/ui/Buttons/ButtonIcon';
import Icon from '@snowit/ui/Icon';
import Loader from '@snowit/ui/Loaders/LoaderSpinner';
import Typo from '@snowit/ui/Typography/Typo';
import fuzzy from '@snowit/utils/fuzzy';

import { m } from '../../utils/Motion';

import cx from 'classnames';
import s from './InputMulti.module.scss';

export type InputMultiOption<T> = IInputOption<T> & { search: string; divider?: boolean };

export type IInputMulti<T extends any> = Omit<IInput, 'value'> & {
  values?: string[];
  valueLabel?: (opt: InputMultiOption<T>) => string;
  options: (InputMultiOption<T> | string)[];
  allowReuse?: boolean;
  onSelect: (values: string[], event?: MouseEvent<HTMLLIElement>) => void;
  classNames?: Classnames<
    'wrapper' | 'wrapInput' | 'input' | 'dropdown' | 'bodyDropdown' | 'option'
  >;
};

const InputMulti = <T extends any>(props: IInputMulti<T>) => {
  const {
    label,
    hint,
    placeholder,
    name,
    values = [],
    error,
    errorMsg,
    loading,
    readOnly,
    required,
    disabled,
    onSelect,
    id,
    classNames,
    allowReuse,
    size = 'tr',
  } = props;
  const { minWidth } = useBreakpoints();

  const parent = useRef<HTMLInputElement | null>(null);
  const child = useRef<HTMLLIElement | null>(null);

  const [wParent, setWParent] = useState(0);
  const [showDropdown, setShowDropdown] = useState(false);
  const [searchInput, setSearchInput] = useState<string>('');

  let motionVariants = {};

  if (minWidth.sm) {
    motionVariants = {
      open: { opacity: 1, height: 'auto' },
      collapsed: { opacity: 0, height: 0 },
    };
  } else {
    motionVariants = {
      open: { opacity: 1, translateY: '0%' },
      collapsed: { opacity: 1, translateY: '100%' },
    };
  }

  const handleDropdown = () => setShowDropdown(!showDropdown);

  const options: InputMultiOption<T>[] = props.options
    .filter((opt) => {
      if (typeof opt === 'string') return true;
      return !opt.hidden;
    })
    .map((opt) => {
      if (typeof opt === 'string') {
        return {
          key: opt,
          value: opt,
          label: opt,
          search: opt,
        };
      }
      return opt;
    });

  const handleChange = (e: ChangeEvent<HTMLInputElement>) => {
    setShowDropdown(true);
    setSearchInput(e.target.value);
  };

  const handleRemove = (tag: string, event?: MouseEvent<HTMLDivElement>) => {
    if (event) {
      event.stopPropagation();
    }

    if (!values.includes(tag)) return;
    onSelect(
      values.filter((v) => v !== tag),
      event as unknown as MouseEvent<HTMLLIElement>,
    );
  };

  const isDisabled = (opt: InputMultiOption<T>): boolean => {
    if (opt.disabled) return true;
    if (allowReuse) return false;
    return values.includes(opt.value as string);
  };

  const handleSelect = (
    val: BasicValue,
    option: InputMultiOption<T>,
    event: MouseEvent<HTMLLIElement>,
  ) => {
    if (option.disabled) return;
    if (option.noCta) return;
    const v = String(val ?? '');
    if (!v) return;
    setShowDropdown(false);
    setSearchInput('');
    onSelect([...values, v], event);
  };

  const handleKeyDown = (e: KeyboardEvent<HTMLInputElement>) => {
    const current = e.currentTarget.value;
    if (e.key === 'Enter') {
      const found = options.find((opt) => {
        if (!e.currentTarget.value) return true;
        return fuzzy(e.currentTarget.value, opt.search ?? String(opt.value ?? ''));
      });
      if (found) {
        handleSelect(found.value, found, e as unknown as MouseEvent<HTMLLIElement>);
      }
      return;
    }

    if (e.key === 'Backspace') {
      if (current) return;
      handleRemove(values[values.length - 1]);
    }
  };

  useEffect(() => {
    if (child.current instanceof Element) {
      child.current.scrollIntoView();
    }
  }, [child.current, showDropdown]);

  useLayoutEffect(() => {
    if (parent.current instanceof HTMLElement) {
      setWParent(parent.current.offsetWidth);
    }
  }, []);

  const renderLabel = () => {
    if (!label) return null;
    if (size !== 'tr') return null;
    return <Typo className={s.label} text={label} tag={'label'} size={'df'} />;
  };

  const renderLoader = () => {
    if (!loading) return null;
    return <Loader className={cx(s.iconAfter, s.loader)} size={16} />;
  };

  const renderHint = () => {
    if (error && errorMsg) return null;
    if (!hint) return null;
    return <Typo className={s.hint} text={hint} tag={'p'} size={'md'} color={'text-l'} />;
  };

  const renderError = () => {
    if (!error) return null;
    if (!errorMsg) return null;
    if (error && !errorMsg) return null;
    return <Typo className={s.hint} text={errorMsg} tag={'p'} size={'md'} color={'error'} />;
  };

  const renderTags = () => {
    if (!values.length) return null;
    const opts = values
      .map((val) => {
        const found = options.find((opt) => opt.value === val);
        return found;
      })
      .filter((opt): opt is InputMultiOption<T> => Boolean(opt));
    // const opts = options.filter((opt) => values.includes(opt.value as string));
    return (
      <div className={s.tags}>
        {opts.map((tag, i) => {
          return (
            <BadgeLabel
              key={i}
              className={s.tag}
              label={String(tag.label ?? tag.value ?? '')}
              theme={'rounded'}
              bgColor={'var(--c-bg-d3)'}
              onRemove={(event) => handleRemove(String(tag.value ?? ''), event)}
            />
          );
        })}
      </div>
    );
  };

  const renderHeaderDropdown = () => {
    if (minWidth.sm) return null;
    return (
      <div className={s.headerDropdown}>
        <Typo
          text={label || placeholder || ''}
          tag={'h2'}
          size={'df'}
          weight={'medium'}
          color={'text'}
        />
        <ButtonIcon
          classNames={{ button: s.closeDropdown }}
          icon={'close'}
          onClick={() => setShowDropdown(false)}
          theme={'ghost'}
          size={'db'}
          color={'text'}
          rounded
        />
      </div>
    );
  };

  const renderIconOption = (option: InputMultiOption<T>) => {
    if (!option.icon) return null;
    return <Icon className={s.iconOption} name={option.icon} />;
  };

  const renderLabelOption = (option: InputMultiOption<T>) => {
    return (
      <Typo
        text={option.label ?? ''}
        tag={'p'}
        size={'df'}
        lineHeight={'paragraph'}
        color={'text'}
      />
    );
  };

  const renderHintOption = (option: InputMultiOption<T>) => {
    if (!option.hint) return null;
    const hints = Array.isArray(option.hint) ? option.hint : [option.hint];
    return (
      <Fragment>
        {hints.map((_hint) => (
          <Typo key={_hint} text={_hint} tag={'p'} size={'sm'} color={'text-l'} />
        ))}
      </Fragment>
    );
  };

  const renderContentOption = (option: InputMultiOption<T>) => {
    let content: ReactNode = (
      <Fragment>
        {renderIconOption(option)}
        <div className={s.wrapLabelOption}>
          {renderLabelOption(option)}
          {renderHintOption(option)}
        </div>
      </Fragment>
    );
    if (typeof option.content === 'function') content = option.content();
    if (option.content && typeof option.content !== 'function') content = option.content;

    return <div className={s.defaultItem}>{content}</div>;
  };

  const renderOption = (option: InputMultiOption<T>) => {
    const value = String(option.value ?? '');
    const selected = values.includes(value);

    return (
      <li
        key={option.key}
        className={cx(s.option, classNames?.option, {
          [s.notHoverOption]: option.notHover,
          [s.notBorderOption]: option.notBorder,
          [s.disabledOption]: isDisabled(option),
        })}
        aria-disabled={option.disabled}
        ref={selected ? child : undefined}
        onClick={(event) => handleSelect(option.value, option, event)}
      >
        {renderContentOption(option)}
      </li>
    );
  };

  const renderOptions = () => {
    const opts = options.filter((opt) => {
      if (!searchInput) return true;
      return fuzzy(searchInput, opt.search ?? String(opt.value ?? ''));
    });

    if (!opts.length) {
      return renderOption({
        key: 'no_results',
        search: '',
        label: 'no_results',
      });
    }
    return opts.map(renderOption);
  };

  const renderDropdown = () => {
    return (
      <m.div
        className={cx(s.dropdown, classNames?.dropdown)}
        style={
          {
            '--w-parent': `${wParent}px`,
          } as CSSProperties
        }
        initial='collapsed'
        animate='open'
        exit='collapsed'
        variants={motionVariants}
        transition={{
          type: 'tween',
          duration: 0.25,
          ease: [0.215, 0.61, 0.355, 1],
        }}
      >
        {renderHeaderDropdown()}
        <ul className={cx(s.bodyDropdown, classNames?.bodyDropdown)}>{renderOptions()}</ul>
      </m.div>
    );
  };

  const renderInput = () => {
    return (
      <div className={cx(s.wrapper, classNames?.wrapper)}>
        <div
          ref={parent}
          className={cx(s.wrapInput, classNames?.wrapInput, {
            [s.required]: required,
            [s.readOnly]: readOnly || loading,
            [s.error]: error,
            [s.disabled]: disabled,
          })}
          style={{ '--l-h': `var(--s-${size})` } as CSSProperties}
          onClick={handleDropdown}
        >
          {renderTags()}
          <input
            type={'text'}
            id={id}
            className={cx(s.input, classNames?.input)}
            placeholder={placeholder}
            name={name}
            disabled={disabled || loading}
            onChange={handleChange}
            onKeyDown={handleKeyDown}
            value={searchInput}
          />
          {renderLabel()}
          {renderLoader()}
        </div>
        {renderHint()}
        {renderError()}
      </div>
    );
  };

  return (
    <Fragment>
      {renderInput()}
      <Portal
        mounted={showDropdown}
        content={renderDropdown()}
        bindTo={parent.current}
        onClose={() => setShowDropdown(false)}
        fullscreen={!minWidth.sm}
        clickToHide
        position={'adaptive'}
      />
    </Fragment>
  );
};

export default InputMulti;
